
import { Component, EventEmitter, Output, Input, DestroyRef, inject, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SourceTournament } from '../../models/sourceTournament';
import { ConfigService } from '../../services/config/config.service';
import { ImportService } from '../../services/import/import.service';
import { LoggingService } from '../../services/shared/logging/logging.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface ImportOptions {
  includeCourses: boolean;
  includePlayers: boolean;
  includePointsRaceRankings: boolean;
  pointsRaceName?: string;
}

@Component({
  selector: 'app-import-modal',
  standalone: true,
  templateUrl: './import-modal.component.html',
  styleUrls: ['./import-modal.component.scss'],
  imports: [FormsModule],
})
export class ImportModalComponent {
  @Input() showCoursesOption = true;
  @Input() showPlayersOption = true;

  private readonly configService = inject(ConfigService);
  private readonly importService = inject(ImportService);
  private readonly loggingService = inject(LoggingService);
  private readonly destroyRef = inject(DestroyRef);

  includeCourses = true;
  includePlayers = true;
  includePointsRaceRankings = true;
  pointsRaceName?: string =
    this.configService.config?.defaults.import.pointsRaceName;

  sourceTournament: SourceTournament | null = null;
  isImportingSignal: Signal<boolean> | null = null;
  sourceCourses: Array<{ name?: string; shortName?: string }> = [];

  @Output() importConfirmed = new EventEmitter<ImportOptions>();

  isVisible = false;

  confirmImport(): void {
    this.importConfirmed.emit({
      includeCourses: this.includeCourses,
      includePlayers: this.includePlayers,
      includePointsRaceRankings: this.includePlayers
        ? this.includePointsRaceRankings
        : false,
      pointsRaceName: this.includePlayers ? this.pointsRaceName : undefined,
    });

    this.closeModal();
  }

  openModal(tournament: SourceTournament | null, isImporting?: Signal<boolean>): void {
    this.sourceTournament = tournament;
    this.isImportingSignal = isImporting || null;
    this.isVisible = true;

    if (!tournament?.tournamentId) {
      return;
    }

    this.loadSourcePlayersCount(tournament);
    this.loadSourceCoursesCount(tournament);
  }

  closeModal(): void {
    this.isVisible = false;
  }

  private loadSourcePlayersCount(tournament: SourceTournament): void {
    const tournamentId = tournament.tournamentId;

    if (!tournamentId) {
      return;
    }

    const lpgaTournamentId = tournamentId.toString();

    this.importService
      .getPlayersBySourceTournamentIdEndpoint('LPGA', lpgaTournamentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (players) => {
          if (this.sourceTournament?.tournamentId !== tournamentId) {
            return;
          }

          const totalEntries = players?.length ?? 0;

          this.sourceTournament = {
            ...this.sourceTournament,
            totalEntries,
          };
        },
        error: (error) => {
          const errorMessage =
            error?.message ?? 'Unable to retrieve players from LPGA source.';

          this.loggingService.error(
            `Failed to load LPGA players for tournament ${lpgaTournamentId}: ${errorMessage}`,
            'ImportModalComponent'
          );
        },
      });
  }

  private loadSourceCoursesCount(tournament: SourceTournament): void {
    const tournamentId = tournament.tournamentId;

    if (!tournamentId) {
      return;
    }

    const lpgaTournamentId = tournamentId.toString();

    this.importService
      .getCoursesBySourceTournamentIdEndpoint('LPGA', lpgaTournamentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (courses) => {
          if (this.sourceTournament?.tournamentId !== tournamentId) {
            return;
          }

          const coursesCount = courses?.length ?? 0;
          this.sourceCourses = courses || [];

          this.sourceTournament = {
            ...this.sourceTournament,
            coursesCount,
          } as SourceTournament;
        },
        error: (error) => {
          const errorMessage =
            error?.message ?? 'Unable to retrieve courses from LPGA source.';

          this.loggingService.error(
            `Failed to load LPGA courses for tournament ${lpgaTournamentId}: ${errorMessage}`,
            'ImportModalComponent'
          );
        },
      });
  }
}
